import type { ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';

import { cx } from '../utils/cx';

/**
 * Horizontal alignment, expressed in logical terms.
 *
 * `start`/`end` rather than `left`/`right` is not cosmetic here: the
 * prototypes' tables use `text-right` on numeric columns, which under
 * `dir="rtl"` would push Arabic figures to the wrong edge. DEV §13 requires
 * logical properties, so this maps to `text-align: start|end|center`, which
 * flips with direction on its own.
 */
export type TableAlign = 'start' | 'end' | 'center';

export interface TableProps {
  /**
   * Accessible name for the table. Required rather than optional: a data
   * table with no name is the single most common screen-reader complaint in
   * dashboards, and every table this design system will carry (orders,
   * catalogue, merchant approval queue) has a real name already.
   */
  readonly caption: string;
  /** Render the caption visually, or keep it for assistive tech only. */
  readonly captionVisible?: boolean;
  readonly children: ReactNode;
}

/**
 * Data table (§7.1 primitives), reproducing the dashboard and admin
 * prototypes: uppercase `--ecom-ink-faint` header row on `--ecom-surface-2`,
 * 1px row separators, `--ecom-surface-hover` on row hover.
 *
 * The wrapper scrolls horizontally on its own (`overflow-x: auto`) and is
 * focusable, so a table wider than its container stays reachable by keyboard
 * alone — otherwise a mouse would be the only way to see the last columns.
 */
export function Table({ caption, captionVisible = false, children }: TableProps): ReactNode {
  return (
    <div className="ecom-table__scroll" tabIndex={0} role="group" aria-label={caption}>
      <table className="ecom-table">
        <caption className={cx('ecom-table__caption', !captionVisible && 'ecom-sr-only')}>
          {caption}
        </caption>
        {children}
      </table>
    </div>
  );
}

export interface TableSectionProps {
  readonly children: ReactNode;
}

export function TableHead({ children }: TableSectionProps): ReactNode {
  return <thead className="ecom-table__head">{children}</thead>;
}

export function TableBody({ children }: TableSectionProps): ReactNode {
  return <tbody className="ecom-table__body">{children}</tbody>;
}

export interface TableRowProps {
  readonly selected?: boolean;
  readonly children: ReactNode;
}

export function TableRow({ selected, children }: TableRowProps): ReactNode {
  return (
    <tr
      className="ecom-table__row"
      {...(selected !== undefined ? { 'aria-selected': selected, 'data-selected': selected } : {})}
    >
      {children}
    </tr>
  );
}

export interface TableHeaderCellProps extends Omit<
  ThHTMLAttributes<HTMLTableCellElement>,
  'align' | 'className' | 'scope'
> {
  readonly align?: TableAlign;
  /** Defaults to `col`; pass `row` for a row-header cell. */
  readonly scope?: 'col' | 'row';
  readonly children: ReactNode;
}

export function TableHeaderCell({
  align = 'start',
  scope = 'col',
  children,
  ...rest
}: TableHeaderCellProps): ReactNode {
  return (
    <th scope={scope} className="ecom-table__th" data-align={align} {...rest}>
      {children}
    </th>
  );
}

export interface TableCellProps extends Omit<
  TdHTMLAttributes<HTMLTableCellElement>,
  'align' | 'className'
> {
  readonly align?: TableAlign;
  readonly children: ReactNode;
}

export function TableCell({ align = 'start', children, ...rest }: TableCellProps): ReactNode {
  return (
    <td className="ecom-table__td" data-align={align} {...rest}>
      {children}
    </td>
  );
}
